import { debounce } from "lodash-es";
import { type FC, type RefObject, useCallback, useEffect, useRef } from "react";
import { LoadingDNA } from "widgets";
import { useResizeObserver } from "widgets/hooks";
import type { StoreApi } from "zustand";
import { useShallow } from "zustand/shallow";

import { type GenomeViewerStore, useGenomeViewerStore } from "../../store/store.ts";
import { InfoPopover } from "./tooltip/InfoPopover.tsx";

export const Canvas: FC = () => {
  const ref = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const setReady = useGenomeViewerStore((state) => state.setReady);
  const refreshMain = useGenomeViewerStore((state) => state.refreshMain);
  const setupUserEventListeners = useGenomeViewerStore((state) => state.setupUserEventListeners);
  const disableUserEventListeners = useGenomeViewerStore((state) => state.disableUserEventListeners);
  const hoveringRangeSelectionIndicator = useGenomeViewerStore(
    (state) => state.leftSelectionHovering || state.rightSelectionHovering,
  );
  const selectedTrackType = useGenomeViewerStore(
    useShallow((state) => state.tracks.get(state.contextualMenuData?.trackId ?? "")?.trackType ?? undefined),
  );
  const isLoading = useGenomeViewerStore((state) => !(state.busyList.size === 0 && state.waitingList.size === 0));

  // Memoized event handlers to prevent unnecessary re-renders
  const handlePointerEnter = useCallback(() => {
    useGenomeViewerStore.setState({
      disableKeyboardShortcuts: false,
    });
    refreshMain();
  }, [refreshMain]);

  const handlePointerLeave = useCallback(() => {
    useGenomeViewerStore.setState({
      hoverData: undefined,
      hoveredGroupId: undefined,
      hoveredTackId: undefined,
      disableKeyboardShortcuts: true,
    });
    refreshMain();
  }, [refreshMain]);

  useEffect(() => {
    const canvas = ref.current;

    if (canvas) {
      setReady(canvas);
      setupUserEventListeners();
    }

    return () => {
      disableUserEventListeners();
    };
  }, [setReady, setupUserEventListeners, disableUserEventListeners]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        flex: "1 1 auto",
        overflow: "hidden",
        position: "relative",
        cursor: hoveringRangeSelectionIndicator ? "e-resize" : "default",
      }}
    >
      <canvas
        ref={ref}
        data-testid="main-canvas"
        id="genome-viewer-canvas"
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      />
      <LoadingDNA isLoading={isLoading} />
      <ResizeContainer containerRef={containerRef} />
      <InfoPopover selectedTrackType={selectedTrackType} />
    </div>
  );
};

interface ResizeContainerProps {
  containerRef: RefObject<HTMLElement | null>;
}

const ResizeContainer: FC<ResizeContainerProps> = ({ containerRef }) => {
  const rect = useResizeObserver(containerRef);
  const updateCanvasSize = useGenomeViewerStore((state) => state.updateCanvasSize);

  useEffect(() => {
    if (rect?.width && rect.height) {
      updateCanvasSize(rect.width, rect.height);
    }
  }, [rect?.width, rect?.height, updateCanvasSize]);

  // Debounce panel width resizing
  useEffect(() => {
    if (rect?.width) {
      // Debounce the panel resizing
      useGenomeViewerStore.setState({ isResizingPanel: true });
      handleResizePanelEnd(useGenomeViewerStore.setState);
    }
  }, [rect?.width]);

  return null;
};

const handleResizePanelEnd = debounce((set: StoreApi<GenomeViewerStore>["setState"]) => {
  set({ isResizingPanel: false });
}, 600);
